import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { Account } from '../../../../../../shared/types/modules/account';
import { Accordion } from '../../../../../components/general/accordion';
import { Table } from '../../../../../components/general/table';
import {
    CASES_GENERAL_DETAIL,
    CASES_GENERAL_INFORMATION_DETAIL,
    PQR_DETAIL,
    QUOTATION_DETAIL,
    SARLAFT_DETAIL,
    SWITCH_DETAIL,
} from '../../../../../../routes/paths';
import { IdentificationPlatePolicyFilters } from './filters/IdentificationPlatePolicyFilters';
import { useInteractionsData } from './hooks/useInteractionsData';
import { Channel } from './types';

type Props = {
    title: string;
    Icon: React.ElementType;
    channel: Channel;
    account?: Account;
    defaultExpanded?: boolean;
};

type Row = {
    source:
        | 'SWITCH'
        | 'CASES_GENERAL_INFO'
        | 'QUOTATION'
        | 'SARLAFT'
        | 'CASES_GENERAL'
        | 'PQR';
    caseId?: string | null;
    sarlaftId?: string | null;
};

const sourceUrlBuilders: Record<Row['source'], (r: Row) => string | null> = {
    SWITCH: (r) => (r.caseId ? `${SWITCH_DETAIL}/${r.caseId}` : null),
    CASES_GENERAL_INFO: (r) =>
        r.caseId ? `${CASES_GENERAL_INFORMATION_DETAIL}/${r.caseId}` : null,
    QUOTATION: (r) => (r.caseId ? `${QUOTATION_DETAIL}/${r.caseId}` : null),
    SARLAFT: (r) => (r.sarlaftId ? `${SARLAFT_DETAIL}/${r.sarlaftId}` : null),
    CASES_GENERAL: (r) =>
        r.caseId ? `${CASES_GENERAL_DETAIL}/${r.caseId}` : null,
    PQR: (r) => (r.caseId ? `${PQR_DETAIL}/${r.caseId}` : null),
};

export const HistoryOthersSection = ({
    title,
    Icon,
    channel,
    account,
    defaultExpanded = false,
}: Props) => {
    const [open, setOpen] = useState<boolean>(defaultExpanded);

    const {
        rows,
        rowCount,
        loading,
        columns,
        paginationModel,
        setPaginationModel,
        onSubmitFilters,
        onResetFilters,
        search,
        enable,
    } = useInteractionsData({
        endpoint: 'others',
        channel,
        initialFilters: { identification: account?.document ?? '' },
        pageSize: 20,
        autoload: false,
    });

    const handleAccordionChange = useCallback(
        (_event: React.SyntheticEvent, isOpen: boolean) => {
            setOpen(isOpen);
            if (isOpen) enable();
        },
        [enable]
    );

    const onRowClick = (params: any) => {
        const r = params.row as Row;
        const build = sourceUrlBuilders[r.source];
        const url = build?.(r) ?? null;
        if (url) window.open(url, '_blank');
    };

    return (
        <Accordion
            title={title}
            icon={<Icon sx={{ color: 'primary.main' }} />}
            mt={0}
            expanded={open}
            defaultExpanded={false}
            onChange={handleAccordionChange}
        >
            <IdentificationPlatePolicyFilters
                initialValues={{ identification: account?.document ?? '' }}
                identificationDisabled={!!account?.document}
                submitting={loading}
                onSubmit={onSubmitFilters}
                onSearch={search}
                onReset={() => {
                    onResetFilters();
                }}
            />
            <Box>
                <Table
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onRowClick={onRowClick}
                />
            </Box>
        </Accordion>
    );
};
